import { formatCaseNumber } from "@/api/cases/caseNumber";
import { formatCaseSpecies } from "@/api/cases/speciesLabel";
import type { AgeClass, AnimalSex } from "@/api/cases/CasesApi";
import { CasesApi } from "@/api/cases/CasesApi";
import {
  buildIntakeReasonUpdate,
  intakeReasonLabels,
  intakeReasonOverviewValue,
  intakeReasonViewValue,
  normalizeIntakeReasonIds,
} from "@/api/cases/intakeReasonEdit";
import { useCaseDetail, useIntakeReasons } from "@/api/cases/useCases";
import {
  useCaseBookmark,
  useToggleCaseBookmark,
} from "@/api/caseTable/useCaseTable";
import { CareSitesApi } from "@/api/careSites/CareSitesApi";
import { useMyCareSites } from "@/api/careSites/useCareSites";
import { queryKeys } from "@/api/queryKeys";
import { caseOverviewColumns } from "@/ui/case/caseOverviewColumns";
import { ANIMAL_STATUS_LABEL } from "@/constants/uiLabels";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { FloatingLabelDatePicker } from "@/ui/components/FloatingLabelDatePicker";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { FloatingLabelListPicker } from "@/ui/components/FloatingLabelListPicker";
import {
  formatPlaceLine,
  GeoPlaceAutocompleteField,
} from "@/ui/components/GeoPlaceAutocompleteField";
import { HeaderAction } from "@/ui/components/HeaderAction";
import { CaseFinderSection } from "@/ui/components/CaseFinderSection";
import { CaseRelationsSection } from "@/ui/components/CaseRelationsSection";
import { CareProtocolPanel } from "@/ui/components/care/CareProtocolPanel";
import { clearFocusEntryParam } from "@/ui/components/care/useFocusEntry";
import { InfoField } from "@/ui/components/DenseRow";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { StatusChip, statusToneFor } from "@/ui/components/StatusChip";
import { WorkTabs } from "@/ui/components/WorkTabs";
import {
  consumeFinderCaseNotice,
  readFinderCaseNotice,
} from "@/ui/finder/finderCaseNotice";
import { getShadowStyle } from "@/theme/shadowStyle";
import {
  assessFoundOn,
  formatIsoDateDE,
  uiErrorMessage,
  todayIsoLocal,
} from "@/utils/isoDate";
import { useQuery } from "@tanstack/react-query";
import { useLocalSearchParams, useRouter } from "expo-router";
import { HeartIcon } from "phosphor-react-native";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Pressable, Text, useWindowDimensions, View } from "react-native";

const SEX_OPTIONS = [
  { value: "unknown", label: "Unbekannt" },
  { value: "male", label: "Männlich" },
  { value: "female", label: "Weiblich" },
];
const AGE_OPTIONS = [
  { value: "unclear", label: "Unklar" },
  { value: "baby", label: "Baby" },
  { value: "juvenile", label: "Jungtier" },
  { value: "adult", label: "Adult" },
];

const SEX_LABEL: Record<string, string> = {
  male: "Männlich",
  female: "Weiblich",
  unknown: "Unbekannt",
};
const AGE_LABEL: Record<string, string> = {
  baby: "Baby",
  juvenile: "Jungtier",
  adult: "Adult",
  unclear: "Unklar",
};

type EditGroup = "basis" | "fund" | "housing" | "notes" | null;

function firstParam(value: string | string[] | undefined): string | null {
  if (Array.isArray(value)) return value[0]?.trim() || null;
  const text = value?.trim();
  return text ? text : null;
}

function useFieldCols() {
  const { width } = useWindowDimensions();
  return caseOverviewColumns(width).fields;
}

function FieldGrid({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  const cols = useFieldCols();
  const items = Array.isArray(children) ? children : [children];
  const gap = theme.space.sm;
  const basis =
    cols === 1 ? ("100%" as const) : (`${(100 - (cols - 1) * 2) / cols}%` as const);

  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap,
        alignItems: "flex-start",
      }}
    >
      {items.filter(Boolean).map((child, i) => (
        <View
          key={i}
          style={{
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: cols === 1 ? "100%" : basis,
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          {child}
        </View>
      ))}
    </View>
  );
}

function FieldCell({ label, value }: { label: string; value: string }) {
  return <InfoField label={label} value={value} />;
}

function StatusBadge({ status }: { status: string }) {
  return (
    <StatusChip
      label={ANIMAL_STATUS_LABEL[status] ?? status}
      tone={statusToneFor(status)}
      size="md"
    />
  );
}

function CaseHeader({
  name,
  status,
  sex,
  ageClass,
  pendingNumber,
  careSiteName,
  caseNumber,
  bookmarked,
  onToggleBookmark,
  speciesLabel,
  speciesUnassigned,
}: {
  name: string | null;
  status: string;
  sex: string;
  ageClass: string;
  pendingNumber: boolean;
  careSiteName?: string | null;
  caseNumber: string;
  bookmarked: boolean;
  onToggleBookmark: () => void;
  speciesLabel?: string | null;
  speciesUnassigned?: boolean;
}) {
  const { theme } = useTheme();
  const shadow = getShadowStyle(theme.cardShadow);
  return (
    <View
      style={{
        backgroundColor: theme.color.surface,
        borderRadius: theme.radius.md,
        borderWidth: 1,
        borderColor: theme.color.border,
        padding: theme.space.md,
        gap: theme.space.sm,
        ...shadow,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: theme.space.sm,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
          <Text
            style={{
              color: theme.color.muted,
              fontSize: theme.fontSize.tiny,
              fontWeight: "700",
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            {caseNumber}
            {pendingNumber ? " · Nummer folgt" : ""}
          </Text>
          <Text
            style={{
              color: theme.color.text,
              fontSize: theme.fontSize.h2,
              fontWeight: "800",
            }}
            numberOfLines={1}
          >
            {name?.trim() || "Unbenannt"}
          </Text>
          <Text style={{ color: theme.color.textSecondary, fontSize: theme.fontSize.small }}>
            {speciesLabel ? `${speciesLabel} · ` : ""}
            {SEX_LABEL[sex] ?? sex} · {AGE_LABEL[ageClass] ?? ageClass}
            {careSiteName ? ` · ${careSiteName}` : ""}
          </Text>
          {speciesUnassigned ? (
            <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.tiny }}>
              nicht zugeordnet
            </Text>
          ) : null}
        </View>
        <View style={{ alignItems: "flex-end", gap: theme.space.sm }}>
          <StatusBadge status={status} />
          <Pressable
            onPress={onToggleBookmark}
            hitSlop={8}
            accessibilityLabel={
              bookmarked ? "Von der Merkliste nehmen" : "Auf die Merkliste"
            }
          >
            <HeartIcon
              size={22}
              color={bookmarked ? theme.color.danger : theme.color.muted}
              weight={bookmarked ? "fill" : "regular"}
            />
          </Pressable>
        </View>
      </View>
      {pendingNumber ? (
        <Text style={{ color: theme.color.warningText, fontSize: 12 }}>
          Nummer wird noch vergeben
        </Text>
      ) : null}
    </View>
  );
}

export default function AnimalCaseScreen() {
  const raw = useLocalSearchParams<{
    caseId?: string | string[];
    tab?: string | string[];
    focus?: string | string[];
  }>();
  const router = useRouter();
  const caseId = firstParam(raw.caseId);
  const openProtocol = firstParam(raw.tab) === "care";
  const focusEntry = openProtocol && firstParam(raw.focus) === "entry";
  const clearFocusEntry = useCallback(() => {
    clearFocusEntryParam(router);
  }, [router]);
  const { theme } = useTheme();
  const cols = useFieldCols();
  const {
    animalCase,
    intakeReasonIds,
    intakeReasonsStatus,
    loading,
    error,
    refetch,
  } = useCaseDetail(caseId);
  const { bookmarked } = useCaseBookmark(caseId);
  const toggleBookmark = useToggleCaseBookmark();
  const { reasons, loading: catalogLoading } = useIntakeReasons(animalCase?.organization_id);
  const { sites } = useMyCareSites();

  const [arrivalNotice] = useState<string | null>(() => readFinderCaseNotice(caseId));
  const [editGroup, setEditGroup] = useState<EditGroup>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [foundWarning, setFoundWarning] = useState<string | null>(null);
  const [foundOnFieldValid, setFoundOnFieldValid] = useState(true);
  const onFoundOnValidityChange = useCallback((valid: boolean) => {
    setFoundOnFieldValid(valid);
  }, []);
  const [akteTab, setAkteTab] = useState<"overview" | "care">(
    openProtocol ? "care" : "overview",
  );

  const [name, setName] = useState("");
  const [sex, setSex] = useState<AnimalSex>("unknown");
  const [ageClass, setAgeClass] = useState<AgeClass>("unclear");
  const [foundOn, setFoundOn] = useState<string | null>(null);
  const [foundPlaceId, setFoundPlaceId] = useState<string | null>(null);
  const [foundPlaceText, setFoundPlaceText] = useState("");
  const [foundPlaceExtra, setFoundPlaceExtra] = useState("");
  const [selectedReasons, setSelectedReasons] = useState<Set<string>>(new Set());
  const [reasonsTouched, setReasonsTouched] = useState(false);
  const reasonsTouchedRef = useRef(false);
  reasonsTouchedRef.current = reasonsTouched;
  const [reasonNote, setReasonNote] = useState("");
  const [notes, setNotes] = useState("");
  const [careSiteId, setCareSiteId] = useState<string | null>(null);
  const [placeId, setPlaceId] = useState<string | null>(null);

  useEffect(() => {
    if (!caseId) return;
    consumeFinderCaseNotice(caseId);
  }, [caseId]);

  useEffect(() => {
    if (!animalCase) return;
    setName(animalCase.name ?? "");
    setSex(animalCase.sex);
    setAgeClass(animalCase.age_class);
    setFoundOn(animalCase.found_on);
    setFoundPlaceId(animalCase.found_place_id);
    setFoundPlaceText(animalCase.found_place_name ?? "");
    setFoundPlaceExtra(animalCase.found_place_text ?? "");
    setReasonNote(animalCase.intake_reason_note ?? "");
    setNotes(animalCase.general_notes ?? "");
    setCareSiteId(animalCase.current_care_site_id);
    setPlaceId(animalCase.current_place_id);
  }, [animalCase]);

  useEffect(() => {
    if (intakeReasonIds == null || reasonsTouchedRef.current) return;
    setSelectedReasons(new Set(normalizeIntakeReasonIds(intakeReasonIds)));
    setReasonsTouched(false);
  }, [intakeReasonIds]);

  useEffect(() => {
    const a = assessFoundOn(foundOn);
    setFoundWarning(a.warning ?? null);
  }, [foundOn]);

  const nodesQ = useQuery({
    queryKey: queryKeys.careSites.nodes(careSiteId ?? ""),
    queryFn: () => CareSitesApi.listPlaceNodes(careSiteId!),
    enabled: !!careSiteId && editGroup === "housing",
  });

  const siteOptions = useMemo(
    () => sites.map((s) => ({ value: s.id, label: s.name })),
    [sites]
  );
  const placeOptions = useMemo(() => {
    const nodes = nodesQ.data ?? [];
    return [
      { value: null as string | null, label: "Noch nicht zugeordnet" },
      ...nodes
        .filter((n) => n.is_occupiable)
        .map((n) => ({ value: n.id, label: n.name })),
    ];
  }, [nodesQ.data]);

  const reasonLabels = intakeReasonLabels(reasons, intakeReasonIds);
  const reasonViewValue = intakeReasonOverviewValue(
    intakeReasonsStatus,
    reasonLabels,
    catalogLoading,
  );
  const reasonsLocked = intakeReasonIds == null;

  const placeLabelQ = useQuery({
    queryKey: ["cases", "placeLabel", animalCase?.current_place_id ?? ""],
    queryFn: async () => {
      if (!animalCase?.current_care_site_id || !animalCase.current_place_id)
        return null;
      const nodes = await CareSitesApi.listPlaceNodes(
        animalCase.current_care_site_id
      );
      return (
        nodes.find((n) => n.id === animalCase.current_place_id)?.name ?? null
      );
    },
    enabled: !!animalCase?.current_place_id,
  });

  const foundPlaceMain = animalCase?.found_place_name?.trim()
    ? animalCase.found_place_name
    : animalCase?.found_place_text?.trim() || "";
  const foundPlaceExtraRead =
    animalCase?.found_place_name?.trim() &&
    animalCase?.found_place_text?.trim()
      ? animalCase.found_place_text.trim()
      : null;

  const applyStoredReasons = (ids: readonly string[] | null) => {
    reasonsTouchedRef.current = false;
    setReasonsTouched(false);
    setSelectedReasons(new Set(normalizeIntakeReasonIds(ids)));
  };

  const toggleReason = (id: string) => {
    if (intakeReasonIds == null) return;
    setReasonsTouched(true);
    setSelectedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const cancelEdit = () => {
    setEditGroup(null);
    setMessage(null);
    setFoundOnFieldValid(true);
    if (animalCase) {
      setName(animalCase.name ?? "");
      setSex(animalCase.sex);
      setAgeClass(animalCase.age_class);
      setFoundOn(animalCase.found_on);
      setFoundPlaceId(animalCase.found_place_id);
      setFoundPlaceText(animalCase.found_place_name ?? "");
      setFoundPlaceExtra(animalCase.found_place_text ?? "");
      setReasonNote(animalCase.intake_reason_note ?? "");
      setNotes(animalCase.general_notes ?? "");
      setCareSiteId(animalCase.current_care_site_id);
      setPlaceId(animalCase.current_place_id);
      setSelectedReasons(new Set(normalizeIntakeReasonIds(intakeReasonIds)));
      setReasonsTouched(false);
    }
  };

  const saveGroup = async () => {
    if (!caseId || !editGroup) return;
    if (editGroup === "fund" && !foundOnFieldValid) {
      setMessage("Das Funddatum ist ungültig.");
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      if (editGroup === "basis") {
        await CasesApi.updateCaseMasterRpc({
          caseId,
          name,
          sex,
          ageClass,
        });
      } else if (editGroup === "fund") {
        const originalFoundOn = animalCase?.found_on ?? null;
        const unchangedFoundOn = (foundOn ?? null) === originalFoundOn;
        let nextFoundOn = foundOn;
        let clearFoundOn = foundOn == null;
        if (!unchangedFoundOn) {
          const assessed = assessFoundOn(foundOn);
          if (!assessed.ok) {
            setMessage(assessed.error ?? "Ungültiges Funddatum.");
            setSaving(false);
            return;
          }
          nextFoundOn = assessed.date;
          clearFoundOn = assessed.date == null;
        }
        const reasonPatch = buildIntakeReasonUpdate({
          storedIds: intakeReasonIds,
          selectedIds: [...selectedReasons],
          changed: reasonsTouched,
          status: intakeReasonsStatus,
          storedNote: animalCase?.intake_reason_note,
          note: reasonNote,
        });
        if ("blocked" in reasonPatch) {
          setMessage(reasonPatch.message);
          return;
        }
        await CasesApi.updateCaseMasterRpc({
          caseId,
          foundOn: nextFoundOn,
          clearFoundOn,
          foundPlaceId: foundPlaceId ?? undefined,
          clearFoundPlace: !foundPlaceId,
          foundPlaceText: foundPlaceExtra,
          ...reasonPatch,
        });
      } else if (editGroup === "notes") {
        await CasesApi.updateCaseMasterRpc({
          caseId,
          generalNotes: notes,
        });
      } else if (editGroup === "housing") {
        if (!careSiteId) {
          setMessage("Bitte eine Pflegestelle wählen.");
          setSaving(false);
          return;
        }
        await CasesApi.updateCaseMasterRpc({
          caseId,
          careSiteId,
          placeId,
          updateHousing: true,
        });
      }
      await refetch();
      applyStoredReasons(intakeReasonIds);
      setEditGroup(null);
      setMessage("Gespeichert.");
    } catch (e: any) {
      setMessage(uiErrorMessage(e, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  const numberLabel = formatCaseNumber(animalCase?.case_number);
  const species = animalCase ? formatCaseSpecies(animalCase) : null;
  const half = cols >= 2;
  const cardSlot = half
    ? ({
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: "48%",
        minWidth: 0,
        maxWidth: "100%",
      } as const)
    : ({ width: "100%", maxWidth: "100%", minWidth: 0 } as const);
  const editColStyle = half
    ? ({ flexGrow: 1, flexShrink: 1, flexBasis: "48%", minWidth: 0 } as const)
    : ({ width: "100%", maxWidth: "100%", minWidth: 0 } as const);

  return (
    <Screen
      title={numberLabel}
      scroll
      contentStyle={{ paddingBottom: theme.space.xl }}
    >
      <View style={{ gap: theme.space.sm }} accessibilityLabel="tierakte-gruppenuebersicht-p3">
        {arrivalNotice ? (
          <Text accessibilityRole="alert" style={{ color: theme.color.warningText }}>
            {arrivalNotice}
          </Text>
        ) : null}
        {loading && (
          <Text style={{ color: theme.color.textSecondary }}>Laden…</Text>
        )}
        {error && (
          <Text style={{ color: theme.color.dangerText }}>
            {uiErrorMessage(error, "Laden fehlgeschlagen.")}
          </Text>
        )}
        {message && !editGroup && (
          <Text style={{ color: theme.color.muted }}>{message}</Text>
        )}

        {animalCase && (
          <>
            <CaseHeader
              name={animalCase.name}
              status={animalCase.status}
              sex={animalCase.sex}
              ageClass={animalCase.age_class}
              pendingNumber={!animalCase.case_number}
              careSiteName={animalCase.care_site_name}
              caseNumber={numberLabel}
              speciesLabel={species?.label ?? null}
              speciesUnassigned={species?.unassigned ?? false}
              bookmarked={bookmarked}
              onToggleBookmark={() => caseId && toggleBookmark.mutate(caseId)}
            />

            <WorkTabs
              activeId={akteTab}
              onSelect={(id) => setAkteTab(id as "overview" | "care")}
              tabs={[
                {
                  id: "overview",
                  label: "Übersicht",
                  panel: (
                    <View style={{ gap: theme.space.md, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <View
              style={{
                flexDirection: half ? "row" : "column",
                flexWrap: "wrap",
                gap: theme.space.md,
                alignItems: "stretch",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
            <View style={cardSlot}>
            <SectionCard
              title="Basisdaten"
              bodyStyle={{ paddingVertical: theme.space.sm }}
              headerRight={
                editGroup !== "basis" ? (
                  <HeaderAction
                    label="Bearbeiten"
                    onPress={() => {
                      setMessage(null);
                      setEditGroup("basis");
                    }}
                  />
                ) : undefined
              }
            >
              {editGroup === "basis" ? (
                <View style={{ gap: theme.space.sm }}>
                  <FloatingLabelInput
                    label="Name"
                    value={name}
                    onChangeText={setName}
                  />
                  <View
                    style={{
                      flexDirection: half ? "row" : "column",
                      gap: theme.space.sm,
                    }}
                  >
                    <View style={editColStyle}>
                      <FloatingLabelListPicker
                        label="Geschlecht"
                        options={SEX_OPTIONS}
                        value={sex}
                        onChange={(v) => setSex((v as AnimalSex) ?? "unknown")}
                      />
                    </View>
                    <View style={editColStyle}>
                      <FloatingLabelListPicker
                        label="Altersklasse"
                        options={AGE_OPTIONS}
                        value={ageClass}
                        onChange={(v) =>
                          setAgeClass((v as AgeClass) ?? "unclear")
                        }
                      />
                    </View>
                  </View>
                  <EditActions
                    saving={saving}
                    onCancel={cancelEdit}
                    onSave={saveGroup}
                    message={message}
                  />
                </View>
              ) : (
                <View style={{ gap: theme.space.xs }}>
                  <FieldGrid>
                    <FieldCell label="Name" value={animalCase.name ?? "–"} />
                    <FieldCell label="Art" value={species?.label ?? "–"} />
                    <FieldCell
                      label="Geschlecht"
                      value={SEX_LABEL[animalCase.sex] ?? animalCase.sex}
                    />
                    <FieldCell
                      label="Altersklasse"
                      value={
                        AGE_LABEL[animalCase.age_class] ?? animalCase.age_class
                      }
                    />
                  </FieldGrid>
                  {species?.unassigned ? (
                    <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.tiny }}>
                      nicht zugeordnet
                    </Text>
                  ) : null}
                </View>
              )}
            </SectionCard>
            </View>

            <View style={cardSlot}>
            <SectionCard
              title="Fund und Aufnahme"
              bodyStyle={{ paddingVertical: theme.space.sm }}
              headerRight={
                editGroup !== "fund" ? (
                  <HeaderAction
                    label="Bearbeiten"
                    onPress={() => {
                      setMessage(null);
                      applyStoredReasons(intakeReasonIds);
                      setEditGroup("fund");
                    }}
                  />
                ) : undefined
              }
            >
              {editGroup === "fund" ? (
                <View style={{ gap: theme.space.sm }}>
                  <FloatingLabelDatePicker
                    label="Funddatum"
                    value={foundOn}
                    onChange={setFoundOn}
                    onValidityChange={onFoundOnValidityChange}
                    optional
                    allowClear
                    maxIso={todayIsoLocal()}
                    placeholder="Optional"
                  />
                  {foundWarning ? (
                    <Text style={{ color: theme.color.warningText, fontSize: 12 }}>
                      {foundWarning}
                    </Text>
                  ) : null}
                  <GeoPlaceAutocompleteField
                    valuePlaceId={foundPlaceId}
                    valueText={foundPlaceText}
                    onChangeText={(t) => {
                      setFoundPlaceText(t);
                      setFoundPlaceId(null);
                    }}
                    onSelect={(place) => {
                      setFoundPlaceId(place.id);
                      setFoundPlaceText(formatPlaceLine(place));
                    }}
                    placeholder="Fundort oder PLZ suchen"
                    hint="Ort oder Postleitzahl wählen. Eine Straße ist nicht nötig."
                  />
                  <FloatingLabelInput
                    label="Ergänzung zum Fundort"
                    value={foundPlaceExtra}
                    onChangeText={setFoundPlaceExtra}
                  />
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: theme.space.xs,
                    }}
                  >
                    {reasons.map((r) => {
                      const selected = selectedReasons.has(r.id);
                      return (
                        <Pressable
                          key={r.id}
                          disabled={reasonsLocked}
                          onPress={() => toggleReason(r.id)}
                          accessibilityLabel={r.label}
                          accessibilityState={{ selected, disabled: reasonsLocked }}
                          style={{
                            paddingVertical: theme.space.xs,
                            paddingHorizontal: theme.space.sm,
                            borderRadius: theme.radius.md,
                            borderWidth: selected ? 2 : 1,
                            borderColor: selected
                              ? theme.color.primary
                              : theme.color.border,
                            backgroundColor: selected
                              ? theme.color.primary
                              : theme.color.surface,
                            opacity: reasonsLocked ? 0.45 : 1,
                          }}
                        >
                          <Text
                            style={{
                              color: selected
                                ? theme.color.buttonText
                                : theme.color.text,
                              fontWeight: selected ? "700" : "500",
                              fontSize: 13,
                            }}
                          >
                            {r.label}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                  {reasonsLocked ? (
                    <Text style={{ color: theme.color.muted, fontSize: 12 }}>
                      {intakeReasonViewValue(
                        intakeReasonsStatus === "error" ? "error" : "loading",
                        [],
                      )}
                    </Text>
                  ) : null}
                  <FloatingLabelInput
                    label="Ergänzung zum Aufnahmegrund"
                    value={reasonNote}
                    onChangeText={setReasonNote}
                  />
                  <EditActions
                    saving={saving}
                    saveDisabled={!foundOnFieldValid}
                    onCancel={cancelEdit}
                    onSave={saveGroup}
                    message={message}
                  />
                </View>
              ) : (
                <FieldGrid>
                  <FieldCell
                    label="Funddatum"
                    value={formatIsoDateDE(animalCase.found_on)}
                  />
                  <FieldCell label="Fundort" value={foundPlaceMain || "–"} />
                  {foundPlaceExtraRead ? (
                    <FieldCell
                      label="Ergänzung zum Fundort"
                      value={foundPlaceExtraRead}
                    />
                  ) : null}
                  <FieldCell
                    label="Aufnahmegründe"
                    value={reasonViewValue}
                  />
                  {animalCase.intake_reason_note?.trim() ? (
                    <FieldCell
                      label="Ergänzung zum Aufnahmegrund"
                      value={animalCase.intake_reason_note.trim()}
                    />
                  ) : null}
                </FieldGrid>
              )}
            </SectionCard>
            </View>
            </View>

            <View
              style={{
                flexDirection: half ? "row" : "column",
                flexWrap: "wrap",
                gap: theme.space.md,
                alignItems: "stretch",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
            <View style={cardSlot}>
            <SectionCard
              title="Unterbringung"
              bodyStyle={{ paddingVertical: theme.space.sm }}
              headerRight={
                editGroup !== "housing" ? (
                  <HeaderAction
                    label="Unterbringung ändern"
                    onPress={() => {
                      setMessage(null);
                      setEditGroup("housing");
                    }}
                  />
                ) : undefined
              }
            >
              {editGroup === "housing" ? (
                <View style={{ gap: theme.space.sm }}>
                  <View
                    style={{
                      flexDirection: half ? "row" : "column",
                      gap: theme.space.sm,
                    }}
                  >
                    <View style={editColStyle}>
                      <FloatingLabelListPicker
                        label="Pflegestelle"
                        options={siteOptions}
                        value={careSiteId}
                        onChange={(v) => {
                          setCareSiteId(v);
                          setPlaceId(null);
                        }}
                        required
                        placeholder="Pflegestelle wählen…"
                      />
                    </View>
                    <View style={editColStyle}>
                      <FloatingLabelListPicker
                        label="Unterbringungsplatz"
                        options={placeOptions}
                        value={placeId}
                        onChange={setPlaceId}
                        placeholder="Optional"
                      />
                    </View>
                  </View>
                  <EditActions
                    saving={saving}
                    onCancel={cancelEdit}
                    onSave={saveGroup}
                    message={message}
                  />
                </View>
              ) : (
                <FieldGrid>
                  <FieldCell
                    label="Pflegestelle"
                    value={animalCase.care_site_name ?? "–"}
                  />
                  <FieldCell
                    label="Unterbringungsplatz"
                    value={
                      animalCase.current_place_id
                        ? placeLabelQ.data ?? "–"
                        : "Noch nicht zugeordnet"
                    }
                  />
                </FieldGrid>
              )}
            </SectionCard>
            </View>

            <View style={cardSlot}>
            <SectionCard
              title="Notizen"
              bodyStyle={{ paddingVertical: theme.space.sm }}
              headerRight={
                editGroup !== "notes" ? (
                  <HeaderAction
                    label="Bearbeiten"
                    onPress={() => {
                      setMessage(null);
                      setEditGroup("notes");
                    }}
                  />
                ) : undefined
              }
            >
              {editGroup === "notes" ? (
                <View style={{ gap: theme.space.sm }}>
                  <FloatingLabelInput
                    label="Allgemeine Notizen"
                    value={notes}
                    onChangeText={setNotes}
                  />
                  <EditActions
                    saving={saving}
                    onCancel={cancelEdit}
                    onSave={saveGroup}
                    message={message}
                  />
                </View>
              ) : (
                <Text
                  style={{
                    color: animalCase.general_notes?.trim()
                      ? theme.color.text
                      : theme.color.muted,
                    fontSize: animalCase.general_notes?.trim()
                      ? theme.fontSize.body
                      : theme.fontSize.small,
                    fontWeight: animalCase.general_notes?.trim() ? "500" : "400",
                    lineHeight: 22,
                  }}
                >
                  {animalCase.general_notes?.trim() || "Keine Notizen hinterlegt."}
                </Text>
              )}
            </SectionCard>
            </View>
            </View>

            <CaseFinderSection caseId={animalCase.id} />

            <CaseRelationsSection
              caseId={animalCase.id}
              organizationId={animalCase.organization_id}
            />
                    </View>
                  ),
                },
                {
                  id: "care",
                  label: "Pflege",
                  panel: (
                    <CareProtocolPanel
                      caseId={animalCase.id}
                      organizationId={animalCase.organization_id}
                      careSiteId={animalCase.current_care_site_id}
                      currentStatus={animalCase.status}
                      currentPlaceId={animalCase.current_place_id}
                      foundOn={animalCase.found_on}
                      focusEntry={focusEntry && akteTab === "care"}
                      onFocusEntry={clearFocusEntry}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </View>
    </Screen>
  );
}

function EditActions({
  saving,
  saveDisabled = false,
  onCancel,
  onSave,
  message,
}: {
  saving: boolean;
  saveDisabled?: boolean;
  onCancel: () => void;
  onSave: () => void;
  message: string | null;
}) {
  const { theme } = useTheme();
  return (
    <View style={{ gap: theme.space.xs, marginTop: theme.space.xs }}>
      {message ? (
        <Text style={{ color: theme.color.muted }}>{message}</Text>
      ) : null}
      <View
        style={{ flexDirection: "row", gap: theme.space.sm, flexWrap: "wrap" }}
      >
        <AppButton
          variant="outline"
          onPress={onCancel}
          disabled={saving}
          accessibilityRole="button"
          accessibilityLabel="Abbrechen"
        >
          Abbrechen
        </AppButton>
        <AppButton
          onPress={onSave}
          disabled={saving || saveDisabled}
          accessibilityRole="button"
          accessibilityLabel={saving ? "Speichern…" : "Speichern"}
        >
          {saving ? "Speichern…" : "Speichern"}
        </AppButton>
      </View>
    </View>
  );
}
